import './styles.module.css'
import './global.css'
import {
	Routes,
	Route,
	Navigate,
	Outlet,
	useLocation,
} from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect } from 'react'

import { checkAuth, setOnlineUsers, setUserStatuses } from './features/authSlice'

import { Navbar } from './components/navbars/Navbar'
import { KazooSideNavbar } from './components/navbars/KazooSideNavbar'

import { SignupPage } from './pages/auth/SignupPage'
import { KazooDashboardPage } from './pages/dashboard/KazooDashboardPage'
import { ForgotPasswordPage } from './pages/forgotPassword/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/forgotPassword/ResetPasswordPage'
import { Toaster } from 'react-hot-toast'
import { EmailConfirmationPage } from './pages/auth/EmailConfirmationPage'
import { LoginPage } from './pages/auth/LoginPage'
import { GoogleRedirectPage } from './pages/auth/GoogleRedirectPage'
import { UserTimelinePage } from './pages/timeline/UserTimelinePage'
import { ChatPage } from './pages/chat/ChatPage'
import { FriendsPage } from './pages/members/FriendsPage'
import { DemoLoginPage } from './pages/demoLogin/DemoLoginPage'
import { OnboardingAccount } from './pages/onboarding/OnboardingAccount'
import { UserSettingsPage } from './pages/settings/UserSettingsPage'
import { InvitePartner } from './pages/invitePartner/InvitePartnerPage'
import { UserSideNavbar } from './components/navbars/UserSideNavbar'
import { DashboardPage } from './pages/dashboard/Dashboard'
import {
	checkKazooAuth,
	checkKazooAuthVendor,
} from './features/kazooEventSlice'
import { KazooGuestsPage } from './pages/members/KazooGuestsPage'
import { VendorTimelinePage } from './pages/timeline/VendorTimelinePage'
import { VendorKazooInfoPage } from './pages/timeline/VendorKazooInfoPage'
import { ProfilePage } from './pages/profile/ProfilePage'
import { UserNotificationPage } from './pages/notifications/UserNotificationPage'
import { KazooNotificationPage } from './pages/notifications/KazooNotifications'
import { VendorStaffSignupPage } from './pages/auth/VendorStaffSignupPage'
import { InviteStaffPage } from './pages/inviteStaff/InviteStaffPage'
import { UserNavbar } from './components/navbars/UserNavbar'
import { PreRegistrationPage } from './pages/auth/preregistration/PreRegistrationPage'
import { ThanksForRegisteringPage } from './pages/auth/preregistration/ThanksForRegisteringPage'
import { AtomsPage } from './pages/atoms/Atoms'
import TasksPage from './pages/tasks/TasksPage'
import ProposalPage from './pages/proposal/ProposalPage'
import VendorsListPage from './pages/vendors-list/VendorsListPage'
import PlannersListPage from './pages/planners-list/PlannersListPage'
import BudgetTrackerPage from './pages/budget-tracker/BudgetTrackerPage'
import PaymentsPage from './pages/payment/PaymentsPage'
import KazooTasksPage from './pages/tasks/KazooTasksPage'
import { VendorKazooTimelinePage } from './pages/timeline/VendorKazooTimelinePage'
import VendorsTrackerPage from './pages/vendors-tracker/VendorsTrackerPage'
import ProposalDetail from './pages/proposal/ProposalDetail'
import { PlannerTimelinePage } from './pages/timeline/PlannerTimelinePage'
import WeddingPlannerPage from './pages/dashboard/WeddingPlannerPage'
import { VendorWeddingsPage } from './pages/dashboard/VendorWeddingsPage'
import { toastHelper } from './util/toastHelper'
import TimeManagementPage from './pages/time-management/TimeManagementPage'

import Tasks from './pages/v2/Tasks'
import { SuperAdminLayout } from './pages/admin/SuperAdminLayout'
import { SuperAdminDashboard } from './pages/admin/SuperAdminDashboard'
import { AdminConfigsPage } from './pages/admin/configs/AdminConfigsPage'

function App() {
	const dispatch = useDispatch()
	const location = useLocation()
	const link = location.pathname.split('/')

	// this fixes constant rerendering of timeline since it doesn't worry about
	// other fields that may change in state.auth
	const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)
	const user = useSelector((state) => state.auth.user)
	const checkAuthLoading = useSelector((state) => state.auth.checkAuthLoading)
	const authorizedForKazoo = useSelector(
		(state) => state.kazooEvent.authorized
	)
	const authorizedForKazooVendor = useSelector(
		(state) => state.kazooEvent.authorizedVendor
	)
	const checkKazooAuthLoading = useSelector(
		(state) => state.kazooEvent.checkKazooAuthLoading
	)
	const role = useSelector((state) => state.kazooEvent.role)

	const socket = useSelector((s) => s.auth.socket)

	// wait and check authentication
	useEffect(() => {
		dispatch(checkAuth())

		if (link[1] === 'wedding' && link.length === 4) {
			dispatch(checkKazooAuth(link[3]))
		} else if (link[1] === 'vendor-wedding-info' && link.length === 3) {
			dispatch(checkKazooAuthVendor(link[2]))
		} else if (link[1] === 'vendor-staff' && link.length === 4) {
			dispatch(checkKazooAuthVendor(link[3]))
			const kazooId = link[3]
			if (['details', 'vendor-chat'].includes(link[2])) {
				dispatch(checkKazooAuthVendor(kazooId))
			} else {
				dispatch(checkKazooAuth(kazooId))
			}
		}

		if (socket) {
			const onlineUsersHandler = (userIds) => {
				dispatch(setOnlineUsers(userIds))
			}

			const userStatusesHandler = (statuses) => {
				dispatch(setUserStatuses(statuses))
			}

			socket.on('getOnlineUsers', onlineUsersHandler)
			socket.on('getUserStatuses', userStatusesHandler)

			// Send activity updates on user interaction
			const sendActivity = () => {
				socket.emit('userActivity')
			}

			// Track user activity
			window.addEventListener('mousemove', sendActivity)
			window.addEventListener('keydown', sendActivity)
			window.addEventListener('click', sendActivity)
			window.addEventListener('scroll', sendActivity)

			return () => {
				socket.off('getOnlineUsers', onlineUsersHandler)
				socket.off('getUserStatuses', userStatusesHandler)
				window.removeEventListener('mousemove', sendActivity)
				window.removeEventListener('keydown', sendActivity)
				window.removeEventListener('click', sendActivity)
				window.removeEventListener('scroll', sendActivity)
			}
		}
	}, [dispatch, socket, location.pathname])

	// redirect authenticated users to dashboard (super admins → /admin)
	const RedirectAuthenticatedUser = () => {
		if (isAuthenticated && user?.isVerified) {
			return (
				<Navigate to={user?.isSuperAdmin ? '/admin' : '/user-dashboard'} />
			)
		}
		return <Outlet />
	}

	// protected routes that require authentication
	const ProtectedRoutes = () => {
		if (!isAuthenticated) return <Navigate to="/login" />
		if (!user?.isVerified) return <Navigate to="/verify-email" />
		if (user?.onboarding) return <Navigate to="/onboarding-account" />

		return (
			<>
				<main className="afterLoginMain">
					<Outlet />
				</main>
			</>
		)
	}

	const UserRoutes = () => {
		// if(user?.__t === 'vendor-staff') return <Navigate to="/vendor-staff-dashboard" />
		return (
			<>
				<UserSideNavbar />
				<div className="afterLoginMainLayout">
					<UserNavbar />
					<Outlet />
				</div>
			</>
		)
	}

	// make sure a user is part of the wedding for them to access certain pages
	const KazooEventRoutes = () => {
		if (!authorizedForKazoo) return <Navigate to="/user-dashboard" />
		return (
			<>
				<KazooSideNavbar />
				<div className="afterLoginMainLayout">
					<UserNavbar />
					<Outlet />
				</div>
			</>
		)
	}

	const KazooEventRoutesVendor = () => {
		if (!authorizedForKazooVendor) return <Navigate to="/user-dashboard" />
		return (
			<>
				<KazooSideNavbar />
				<div className="afterLoginMainLayout">
					<UserNavbar />
					<Outlet />
				</div>
			</>
		)
	}

	const KazooEventRoutesPlanner = () => {
		// if (!authorizedForKazooPlanner) return <Navigate to="user-dashboard" />
		return (
			<>
				<KazooSideNavbar />
				<div className="afterLoginMainLayout">
					<UserNavbar />
					<Outlet />
				</div>
			</>
		)
	}

	// certain pages (like invite partner, budget) guests should not be able to access
	const KazooRoutesProtectedFromGuests = () => {
		if (role === 'guest')
			return <Navigate to={`/wedding/wedding-dashboard/${link[3]}`} />
		return (
			<>
				<Outlet />
			</>
		)
	}

	// redirect users that are not verifiying their email
	const VerifyEmailRoute = () => {
		return !isAuthenticated || user?.isVerified ? (
			<Navigate
				to={user?.isSuperAdmin ? '/admin' : '/user-dashboard'}
			/>
		) : (
			<Outlet />
		)
	}

	const DefaultUserRoutes = () => {
		if (user?.__t !== 'user') return <Navigate to="/user-dashboard" />
		return <Outlet />
	}

	// redirect users that have completed their tell us more
	const UserOnboardingRoute = () => {
		if (!user?.isVerified) return <Navigate to="/verify-email" />
		return !user?.onboarding ? (
			<Navigate to={user?.isSuperAdmin ? '/admin' : '/user-dashboard'} />
		) : (
			<>
				<UserSideNavbar disabled={true} />
				<Outlet />
			</>
		)
	}

	// Layout for public pages with top navbar
	const PublicRoutes = () => (
		<>
			<main className="beforeLoginMain">
				<Navbar />
				<Outlet />
			</main>
		</>
	)

	const VendorRoutes = () => {
		if (user?.__t !== 'vendor') return <Navigate to="/user-dashboard" />
		return <Outlet />
	}

	const VendorStaffRoutes = () => {
		if (user?.__t !== 'vendor-staff' && user?.__t !== 'planner-staff')
			return <Navigate to="/user-dashboard" />
		return <Outlet />
	}

	const PlannerRoutes = () => {
		if (user?.__t !== 'kazoo-planner')
			return <Navigate to="/user-dashboard" />
		return <Outlet />
	}

	// Conditional staff module access for wedding planners with business-pro-month payment and vendor staff
	const PlannerStaffRoutes = () => {
		const isKazooPlanner = user?.__t === 'kazoo-planner' && user?.payment === 'business-pro-month'
		const isVendorStaff = user?.__t === 'vendor-staff'

		if (!isKazooPlanner && !isVendorStaff)
			return <Navigate to="/user-dashboard" />
		return <Outlet />
	}

	// Display loading screen while checking auth state
	if (
		checkAuthLoading ||
		(link[1] === 'wedding' && link.length === 4 && checkKazooAuthLoading) ||
		(link[1] === 'vendor-wedding-info' &&
			link.length === 3 &&
			checkKazooAuthLoading) ||
		(link[1] === 'vendor-staff' &&
			((link[1] === 'wedding' ||
				['details', 'vendor-chat'].includes(link[2])) &&
				link.length === 4 &&
				checkKazooAuthLoading)
		))
		return (
			<>
				<h1>Loading ...</h1>
				<Toaster />
			</>
		)

	return (
		<>
			{/* <main> */}
			<Routes>
				{/* Temp pages */}
				<Route path="pre-register" element={<PreRegistrationPage />} />
				<Route
					path="thanks-for-registering"
					element={<ThanksForRegisteringPage />}
				/>

				{/* Public routes */}
				<Route element={<PublicRoutes />}>
					{process.env.REACT_APP_NODE_ENV === 'development' && (
						<Route path="demo-login" element={<DemoLoginPage />} />
					)}
					<Route path="/" element={<LoginPage />} />
					<Route
						path="/forgot-password"
						element={<ForgotPasswordPage />}
					/>
					<Route
						path="/reset-password/:token"
						element={<ResetPasswordPage />}
					/>
					<Route path="/atoms" element={<AtomsPage />} />

					{/* Redirect authenticated users from login/signup */}
					<Route element={<RedirectAuthenticatedUser />}>
						<Route path="/signup" element={<SignupPage />} />
						<Route path="/login" element={<LoginPage />} />
						<Route
							path="/vendor-staff-signup"
							element={<VendorStaffSignupPage />}
						/>
					</Route>

					{/* Allow access to verify-email only if not yet verified */}
					<Route element={<VerifyEmailRoute />}>
						<Route
							path="/verify-email"
							element={<EmailConfirmationPage />}
						/>
					</Route>
				</Route>

				{/* Onboarding flow for users needing to select role / fill details */}
				<Route element={<UserOnboardingRoute />}>
					<Route
						path="/onboarding-account"
						element={<OnboardingAccount />}
					/>
				</Route>

				{/* Google OAuth redirect handler */}
				<Route
					path="/google-redirect"
					element={<GoogleRedirectPage />}
				/>

				<Route path="/admin" element={<SuperAdminLayout />}>
					<Route index element={<SuperAdminDashboard />} />
					<Route path="configs/*" element={<AdminConfigsPage />} />
				</Route>

				{/* Private routes */}
				<Route element={<ProtectedRoutes />}>
					<Route element={<UserRoutes />}>
						<Route
							path="/settings"
							element={<UserSettingsPage />}
						/>
						<Route
							path="/user-dashboard"
							element={<DashboardPage />}
						/>
						<Route
							path="/profile/:userId"
							element={<ProfilePage />}
						/>
						<Route
							path="/notifications"
							element={<UserNotificationPage />}
						/>
						{/* venders-list page */}
						<Route
							path="/vendors-list"
							element={<VendorsListPage type="vendor" />}
						/>
						<Route
							path="/venues-list"
							element={<VendorsListPage type="venue" />}
						/>
						<Route
							path="/planners-list"
							element={<PlannersListPage />}
						/>
						<Route path="/chat" element={<ChatPage />} />

						{/* Default user exclusive routes (bride, groom, guests) */}
						<Route element={<DefaultUserRoutes />}>
							<Route path="/members" element={<FriendsPage />} />
						</Route>

						{/* Vendor exclusive routes */}
						<Route element={<VendorRoutes />}>
							<Route path="/vendor-friends" element={<FriendsPage />} />
							<Route
								path="/vendor-weddings"
								element={<VendorWeddingsPage />}
							/>
							<Route
								path="/vendor-timeline"
								element={<VendorTimelinePage />}
							/>
							<Route
								path="/invite-staff"
								element={<InviteStaffPage />}
							/>
							<Route
								path="/vendor-tasks"
								element={<TasksPage />}
							/>
							<Route
								path="/vendor-proposal"
								element={<ProposalPage />}
							/>
							<Route
								path="/vendor-proposal/:proposalId"
								element={<ProposalDetail />}
							/>
							{/* <Route
								path="/vendor-wedding/details/:kazooId"
								element={<VendorKazooInfoPage />}
							/> */}
							{/* TODO: uncomment when implemented */}
							{/* <Route
								path="/vendor-payments"
								element={<PaymentsPage />}
							/> */}
						</Route>
						{/* Vendor Staff routes */}
						<Route element={<VendorStaffRoutes />}>
							{/**
							 * TODO:: update according to API response
							 * TEMP to view UI
							 */}
							<Route
								path="/vendor-staff/weddings"
								element={<VendorWeddingsPage isStaff={true} />}
							/>
							<Route
								path="/vendor-staff/timeline"
								element={<VendorTimelinePage isStaff={true} />}
							/>

							<Route
								path="/vendor-staff/tasks"
								element={<Tasks isStaff={true} />}
							/>
							<Route
								path="/vendor-staff/time-management"
								element={<TimeManagementPage />}
							/>
							{/* <Route
								path="/vendor-staff/wedding/details/:kazooId"
								element={<VendorKazooInfoPage isStaff={true}/>}
							/> */}
						</Route>

						{/* Planner exclusive routes */}
						<Route element={<PlannerRoutes />}>
							<Route path="/planner-friends" element={<FriendsPage />} />
							<Route
								path="/planner-timeline"
								element={<PlannerTimelinePage />}
							/>
							{/**
							 * TODO:: Planner Tasks update according to API response
							 * TEMP to view UI
							 */}
							<Route
								path="/planner-weddings"
								element={<WeddingPlannerPage />}
							/>
							<Route
								path="/planner-invite-staff"
								element={<InviteStaffPage />}
							/>
							<Route
								path="/planner-tasks"
								element={<TasksPage />}
							/>
							{/* TODO: uncomment when implemented */}
							{/* <Route
								path="/planner-payments"
								element={<PaymentsPage />}
							/> */}
							<Route
								path="/planner-proposal"
								element={<ProposalPage />}
							/>
						</Route>

						{/* Planner staff module routes (requires business-pro-month payment) and vendor staff */}
						<Route element={<PlannerStaffRoutes />}>
							<Route
								path="/planner-invite-staff"
								element={<InviteStaffPage />}
							/>
							<Route
								path="/vendor-staff-invite-staff"
								element={<InviteStaffPage />}
							/>
						</Route>
					</Route>
					<Route element={<KazooEventRoutes />}>
						{/* Kazoo page */}
						<Route
							path="/wedding/wedding-dashboard/:kazooId"
							element={<KazooDashboardPage />}
						/>
						<Route path="/wedding/chat/:kazooId" element={<ChatPage />} />

						{/* Routes that are protected from guests */}
						<Route element={<KazooRoutesProtectedFromGuests />}>
							<Route
								path="/wedding/timeline/:kazooId"
								element={<UserTimelinePage />}
							/>
							<Route
								path="/wedding/invite-your-partner/:kazooId"
								element={<InvitePartner />}
							/>
							<Route
								path="/wedding/tasks/:kazooId"
								element={<KazooTasksPage />}
							/>
							<Route
								path="/wedding/wedding-guests/:kazooId"
								element={<KazooGuestsPage />}
							/>
							<Route
								path="/wedding/vendors/:kazooId"
								element={<VendorsTrackerPage />}
							/>
							<Route
								path="/wedding/notifications/:kazooId"
								element={<KazooNotificationPage />}
							/>
							<Route
								path="/wedding/budget-tracker/:kazooId"
								element={<BudgetTrackerPage />}
							/>
							<Route path="/wedding/chat/:kazooId" element={<ChatPage />} />
							{/* <Route element={<UserPaygatedRoutes />}>
							</Route> */}
						</Route>
					</Route>
					<Route element={<KazooEventRoutesVendor />}>
						<Route
							path="/wedding/details/:kazooId"
							element={<VendorKazooInfoPage />}
						/>
						{/* TODO: uncomment when implemented */}
						{/* <Route
							path="/wedding/vendor-timeline/:kazooId"
							element={<VendorKazooTimelinePage />}
						/>
						<Route path="/wedding/staff-list/:kazooId" />
						<Route path="/wedding/vendor-tasks/:kazooId" />
						<Route path="/wedding/vendor-payments/:kazooId" />
						<Route path="/wedding/vendor-proposal/:kazooId" /> */}
						<Route path="/wedding/vendor-tasks/:kazooId" element={<KazooTasksPage />} />
						<Route path="/wedding/vendor-chat/:kazooId" element={<ChatPage />} />
					</Route>

					{/**
					 * TODO:: Planner Tasks update according to API response
					 * TEMP to view UI
					 */}
					<Route element={<KazooEventRoutesPlanner />}>
						<Route
							path="/planner-wedding-info/:kazooId"
							element={
								/* TODO:: Update it according to API */
								<VendorKazooInfoPage />
							}
						/>
						<Route
							path="/wedding-planner-tasks/:kazooId"
							element={<TasksPage />}
						/>
						<Route
							path="/wedding-planner-budget/:kazooId"
							element={<BudgetTrackerPage />}
						/>
						<Route
							path="/wedding-planner-timeline/:kazooId"
							element={<PlannerTimelinePage title="Itinerary" />}
						/>
						<Route
							path="/wedding-planner-guests/:kazooId"
							element={<KazooGuestsPage />}
						/>
						<Route
							path="/wedding-planner-vendor-tracker/:kazooId"
							element={<VendorsTrackerPage />}
						/>
					</Route>
				</Route>

				<Route path="*" element={<Navigate to="/user-dashboard" replace />} />
			</Routes>

			{/* Global toast notification system */}
			<Toaster />
			{/* </main> */}
		</>
	)
}

export default App
